import { useMutation, useQuery } from '@tanstack/react-query';
import {
	getAllNotDeletedProjects,
	getGeneratableDocumentTemplateAssignedProjects,
	unassignProjectFromGeneratableDocumentTemplate,
} from '@dealstack/frontend/shared/dataExchange/services/god.service.ts';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import { useMemo } from 'react';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';

export const useLinkedProjectsTable = (templateId: string) => {
	const getLinkedProjects = useQuery({
		queryKey: [
			API_PATHS.GODMODE_GENERATABLE_DOCUMENTS_TEMPLATE_ID_ASSIGNED_PROJECTS_GET,
			{ id: templateId },
		],

		queryFn: ({ signal }) =>
			getGeneratableDocumentTemplateAssignedProjects(
				API_PATHS.GODMODE_GENERATABLE_DOCUMENTS_TEMPLATE_ID_ASSIGNED_PROJECTS_GET,
				{ id: templateId },
				signal
			),
	});
	const getAllProjectsRequest = useQuery({
		queryKey: [API_PATHS.GODMODE_PROJECTS_GET],
		queryFn: ({ signal }) => getAllNotDeletedProjects(API_PATHS.GODMODE_PROJECTS_GET, signal),
	});

	const unassignProjectRequest = useMutation({
		mutationFn: unassignProjectFromGeneratableDocumentTemplate,
	});

	const onUnassignProject = (dealId: string) => {
		unassignProjectRequest.mutate(
			{ templateId, dealId },
			{
				onSuccess: async () => {
					showToastV2({ title: 'Project unlinked successfully' });
					await getLinkedProjects.refetch();
				},
			}
		);
	};

	const availableProjects = useMemo(() => {
		const allProjects = getAllProjectsRequest.data ?? [];

		const linkedProjectsIds = getLinkedProjects?.data?.map(
			(linkedProject) => linkedProject.dealId
		);

		return allProjects.filter((project) => !linkedProjectsIds?.includes(project.id));
	}, [getAllProjectsRequest.data, getLinkedProjects.data]);

	return {
		availableProjects,
		onUnassignProject,
		isLoading: getLinkedProjects.isFetching,
		linkedProjects: getLinkedProjects?.data ?? [],
		isAllProjectsLinked: availableProjects.length === 0,
		isActionsLoading: unassignProjectRequest.isPending,
		refetchLinkedProjects: getLinkedProjects.refetch,
	};
};
